import React, { useEffect, useState } from 'react';
import { getOrgs, getMachines, getMachineShares, createShare, deleteShare, updateOrg } from '../../API/adminAPI';
import OrgWizard from './OrgWizard';

const Icon = ({ d, size = 15, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const EditIcon    = 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z';
const PlusIcon    = 'M12 5v14 M5 12h14';
const SearchIcon  = 'M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z';
const RefreshIcon = 'M1 4v6h6 M23 20v-6h-6 M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15';
const ShareIcon   = 'M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8 M16 6l-4-4-4 4 M12 2v13';
const CloseIcon   = 'M18 6L6 18 M6 6l12 12';

const TYPE_STYLES = {
  atoms:       { bg: 'var(--ai-soft)',     text: 'var(--role-atoms)', label: 'ATOMS' },
  oem:         { bg: 'var(--accent-soft)', text: 'var(--accent)',     label: 'OEM' },
  distributor: { bg: 'var(--warn-bg)',     text: 'var(--warn)',       label: 'Distributor' },
};

// ── Share drawer ──────────────────────────────────────────────────────────────
function ShareDrawer({ org, onClose }) {
  const [machines,  setMachines]  = useState([]);
  const [shares,    setShares]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  const loadShares = async (list) => {
    try {
      const results = await Promise.all((list || machines).map(m => getMachineShares(m.id).then(sd => ({ m, sd }))));
      const all = [];
      for (const { m, sd } of results)
        for (const s of (sd.shares || []))
          if (s.to_org_id === org.id) all.push({ ...s, machine: m });
      setShares(all);
    } catch { setShares([]); }
  };

  useEffect(() => {
    getMachines().then(md => {
      const list = md.machines || [];
      setMachines(list);
      return loadShares(list);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const sharedIds = new Set(shares.map(s => s.machine.id));
  const unshared  = machines.filter(m => !sharedIds.has(m.id));

  const addShare = async (machineId) => {
    setSaving(true);
    try { await createShare(machineId, { to_org: org.id, permitted_channels: [], is_active: true }); await loadShares(machines); }
    catch (e) { setError(e?.response?.data?.error || 'Share failed'); }
    finally { setSaving(false); }
  };

  const removeShare = async (share) => {
    setSaving(true);
    try { await deleteShare(share.machine.id, share.id); await loadShares(machines); }
    catch (e) { setError(e?.response?.data?.error || 'Remove failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex' }}>
      <div style={{ flex: 1, background: 'rgba(12,14,22,.32)', backdropFilter: 'blur(3px)' }} onClick={onClose} />
      <div style={{ width: 480, background: 'var(--bg-surface)', borderLeft: '1px solid var(--border-hair)', display: 'flex', flexDirection: 'column', overflowY: 'auto', boxShadow: 'var(--sh-lg)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-hair)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 15, color: 'var(--ink-900)' }}>Machine Shares — {org.name}</div>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>Machines shared appear under this distributor's branding</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-500)', padding: 4 }}>
            <Icon d={CloseIcon} size={18} />
          </button>
        </div>
        <div style={{ flex: 1, padding: '20px 24px' }}>
          {error && <div style={{ background: 'var(--offline-bg)', color: 'var(--offline)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>{error}</div>}
          {loading ? (
            <div style={{ textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13, padding: '32px 0' }}>Loading…</div>
          ) : (
            <div>
              {shares.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7, color: 'var(--online)', marginBottom: 10 }}>Currently Shared</div>
                  {shares.map(s => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 'var(--r-md)', background: 'var(--online-bg)', border: '1px solid var(--online-border)', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', background: 'var(--accent-soft)', padding: '2px 7px', borderRadius: 4 }}>{s.machine.machine_id}</span>
                        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-900)' }}>{s.machine.machine_name || s.machine.machine_id}</span>
                        {!s.is_active && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--offline)' }}>Suspended</span>}
                      </div>
                      <button onClick={() => removeShare(s)} disabled={saving}
                        style={{ background: 'none', border: '1px solid var(--offline-border)', borderRadius: 'var(--r-sm)', padding: '4px 10px', fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--offline)', cursor: 'pointer' }}>
                        Remove
                      </button>
                    </div>
                  ))}
                  <div style={{ height: 1, background: 'var(--border-hair)', margin: '14px 0' }} />
                </div>
              )}
              {unshared.length > 0 && (
                <div>
                  <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7, color: 'var(--ink-500)', marginBottom: 10 }}>Add Machine</div>
                  {unshared.map(m => (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--border-hair)', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)', background: 'var(--bg-raised)', padding: '2px 7px', borderRadius: 4 }}>{m.machine_id}</span>
                        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-900)' }}>{m.machine_name || m.machine_id}</span>
                        {m.owned_by && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--accent)' }}>{m.owned_by}</span>}
                      </div>
                      <button onClick={() => addShare(m.id)} disabled={saving}
                        style={{ background: 'var(--accent-soft)', border: '1px solid rgba(8,151,190,0.2)', borderRadius: 'var(--r-sm)', padding: '4px 12px', fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }}>
                        Share
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {shares.length === 0 && unshared.length === 0 && (
                <div style={{ textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13, padding: '24px 0' }}>No machines available</div>
              )}
            </div>
          )}
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border-hair)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', background: 'var(--accent)', border: 'none', borderRadius: 'var(--r-md)', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: '#fff', cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────
const SuperOrgsTab = () => {
  const [orgs,      setOrgs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [wizardOrg, setWizardOrg] = useState(undefined);
  const [shareOrg,  setShareOrg]  = useState(null);

  const load = async () => {
    setLoading(true); setError('');
    try { const d = await getOrgs(); setOrgs(d.organisations || []); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (org) => {
    try { await updateOrg(org.id, { is_active: !org.is_active }); load(); }
    catch (e) { alert(e?.response?.data?.error || 'Toggle failed'); }
  };

  const filtered = orgs.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));
  const grouped  = {
    atoms:       filtered.filter(o => o.org_type === 'atoms'),
    oem:         filtered.filter(o => o.org_type === 'oem'),
    distributor: filtered.filter(o => o.org_type === 'distributor'),
  };

  return (
    <div>
      {error && <div style={{ background: 'var(--offline-bg)', color: 'var(--offline)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 20 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-300)', pointerEvents: 'none' }}><Icon d={SearchIcon} /></div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search organisations…"
            style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-700)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', padding: '7px 10px', background: 'none', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', cursor: 'pointer', color: 'var(--ink-500)' }}>
            <Icon d={RefreshIcon} size={14} />
          </button>
          <button onClick={() => setWizardOrg(null)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--accent)', border: 'none', borderRadius: 'var(--r-md)', cursor: 'pointer', color: '#fff', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500 }}>
            <Icon d={PlusIcon} size={14} color="#fff" />
            New Organisation
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {['atoms', 'oem', 'distributor'].map(type => {
            const list = grouped[type];
            if (!list.length) return null;
            const ts = TYPE_STYLES[type] || TYPE_STYLES.oem;
            return (
              <div key={type} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-hair)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
                {/* Section header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: '1px solid var(--border-hair)', background: 'var(--bg-raised)' }}>
                  <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 13, color: 'var(--ink-900)' }}>{ts.label}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, background: ts.bg, color: ts.text, borderRadius: 4, padding: '2px 7px' }}>{list.length}</span>
                  {type === 'distributor' && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)' }}>— use share icon to configure machine access</span>}
                </div>
                {/* Table header */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr 100px 80px', padding: '10px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
                  {['Name', 'Brand Colour', 'Parent OEM', 'Status', 'Actions'].map((h, i) => (
                    <div key={i} style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--ink-500)' }}>{h}</div>
                  ))}
                </div>
                {list.map(o => (
                  <div key={o.id} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr 100px 80px', padding: '13px 20px', borderBottom: '1px solid var(--border-hair)', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {o.logo_url && <img src={o.logo_url} alt="" style={{ height: 22, objectFit: 'contain', borderRadius: 3 }} />}
                      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-900)' }}>{o.name}</span>
                      {!o.is_active && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'var(--offline)', background: 'var(--offline-bg)', borderRadius: 4, padding: '1px 6px' }}>Suspended</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 14, height: 14, borderRadius: 3, background: o.primary_color || '#0897BE', border: '1px solid var(--border-subtle)', flexShrink: 0 }} />
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)' }}>{o.primary_color || '—'}</span>
                    </div>
                    <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)' }}>{o.parent_oem || '—'}</div>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
                        <div
                          onClick={() => toggleActive(o)}
                          style={{ width: 32, height: 18, borderRadius: 9, background: o.is_active ? 'var(--online)' : 'var(--border-strong)', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                          <div style={{ position: 'absolute', top: 2, left: o.is_active ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                        </div>
                        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: o.is_active ? 'var(--online)' : 'var(--offline)' }}>{o.is_active ? 'Active' : 'Suspended'}</span>
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => setWizardOrg(o)} title="Edit"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-500)', padding: 4, borderRadius: 'var(--r-sm)' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-500)'}>
                        <Icon d={EditIcon} />
                      </button>
                      {type === 'distributor' && (
                        <button onClick={() => setShareOrg(o)} title="Manage machine shares"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 4, borderRadius: 'var(--r-sm)' }}>
                          <Icon d={ShareIcon} color="var(--accent)" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          {filtered.length === 0 && !loading && (
            <div style={{ textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13, padding: '48px 0' }}>No organisations found</div>
          )}
        </div>
      )}

      {wizardOrg !== undefined && (
        <OrgWizard org={wizardOrg} onClose={(saved) => { setWizardOrg(undefined); if (saved) load(); }} />
      )}
      {shareOrg && (
        <ShareDrawer org={shareOrg} onClose={() => { setShareOrg(null); load(); }} />
      )}
    </div>
  );
};

export default SuperOrgsTab;
